import React from "react";
import SEO from "@/components/SEO";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { BLOGS } from "@/data/content";
import { buildArticleSchema } from "@/utils/schema";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";

export default function BlogDetail() {
  const { slug } = useParams();
  const post = BLOGS.find((b) => b.slug === slug) || BLOGS[0];
  const others = BLOGS.filter((b) => b.slug !== post.slug).slice(0, 3);
  const extraImgs = post.images || [];
  const articleLd = buildArticleSchema({
    title: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    urlPath: `/blogs/${post.slug}`,
  });

  return (
    <div data-testid="page-blog-detail" className="bg-[#FAF6F1]">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blogs/${post.slug}`}
        image={post.image}
        type="article"
        keywords={`${post.category}, ${post.title}, wedding planning, event planning India`}
        breadcrumbLast={post.title}
        jsonLd={articleLd}
      />
<article>
        <section className="pt-28 md:pt-32 pb-8 bg-[#F4DCD8]/30">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <Link to="/blogs" className="link-gold text-[12px] tracking-[0.18em] uppercase text-[#B65980] inline-flex font-semibold" data-testid="blog-back">
              <ArrowLeft size={14}/> Back to Insights
            </Link>
            <p className="pill mt-6">{post.category}</p>
            <h1 className="font-display text-4xl md:text-6xl mt-4 text-[#3B312D] leading-[1.05] max-w-3xl">{post.title}</h1>
            <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-[#3B312D]/70 font-semibold">{post.date} · {post.read}</p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-8">
          <div className="rounded-[22px] overflow-hidden">
            <Img imageKey={slotKeyFor.blogCover(post)} defaultSrc={post.image} defaultAlt={post.title} className="w-full h-[280px] md:h-[520px]" />
          </div>
        </div>

        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <p className="font-editorial italic text-2xl md:text-3xl text-[#3B312D] leading-relaxed">{post.excerpt}</p>

            {post.body.slice(0, 4).map((p, i) => (
              <p key={i} className="mt-7 text-[17px] leading-[1.85] text-[#3B312D]/90">{p}</p>
            ))}

            {extraImgs[0] && (
              <figure className="my-12 rounded-[22px] overflow-hidden">
                <Img imageKey={slotKeyFor.blogInline(post, 0)} defaultSrc={extraImgs[0]} defaultAlt={`${post.title} — supporting visual 1`} className="w-full h-[320px] md:h-[460px]" />
              </figure>
            )}

            {post.body.slice(4, 6).map((p, i) => (
              <p key={`b-${i}`} className="mt-7 text-[17px] leading-[1.85] text-[#3B312D]/90">{p}</p>
            ))}

            {extraImgs[1] && (
              <figure className="my-12 rounded-[22px] overflow-hidden">
                <Img imageKey={slotKeyFor.blogInline(post, 1)} defaultSrc={extraImgs[1]} defaultAlt={`${post.title} — supporting visual 2`} className="w-full h-[320px] md:h-[460px]" />
              </figure>
            )}

            {post.body.slice(6).map((p, i) => (
              <p key={`c-${i}`} className="mt-7 text-[17px] leading-[1.85] text-[#3B312D]/90">{p}</p>
            ))}

            {post.pointers && (
              <div className="mt-14 rounded-[22px] bg-[#F4DCD8]/50 p-7 md:p-9 border border-[#B65980]/20">
                <p className="pill-rose">Quick Pointers</p>
                <h3 className="font-display text-2xl md:text-3xl mt-4 text-[#3B312D]">Keep these handy.</h3>
                <ul className="mt-5 space-y-3">
                  {post.pointers.map((pt, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15.5px] text-[#3B312D] leading-relaxed">
                      <CheckCircle2 size={18} className="text-[#8A9A7B] mt-1 shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {extraImgs[2] && (
              <figure className="my-14 rounded-[22px] overflow-hidden">
                <Img imageKey={slotKeyFor.blogInline(post, 2)} defaultSrc={extraImgs[2]} defaultAlt={`${post.title} — supporting visual 3`} className="w-full h-[320px] md:h-[460px]" />
              </figure>
            )}

            <div className="mt-14 text-center">
              <p className="font-display text-2xl md:text-3xl text-[#3B312D] leading-snug max-w-xl mx-auto">
                Planning a celebration? Let Petals &amp; Promises help you shortlist the right venue and plan every detail with calm precision.
              </p>
              <Link to="/contact" className="btn-primary mt-7" data-testid="blog-detail-cta">Talk to our team</Link>
            </div>
          </div>
        </section>
      </article>

      <section className="py-14 md:py-20 bg-[#F4DCD8]/35">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Continue reading</p>
          <h2 className="font-display text-3xl md:text-5xl mt-5 text-[#3B312D]">More from the studio</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((b) => (
              <Link key={b.slug} to={`/blogs/${b.slug}`} className="lux-card overflow-hidden flex flex-col h-full" data-testid={`blog-related-${b.slug}`}>
                <div className="h-52 image-zoom shrink-0"><Img imageKey={slotKeyFor.blogCover(b)} defaultSrc={b.image} defaultAlt={b.title} className="w-full h-full" /></div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">{b.category}</p>
                  <h3 className="font-display text-2xl mt-2 text-[#3B312D]">{b.title}</h3>
                  <span className="link-gold mt-4 inline-flex text-[12px] tracking-[0.18em] uppercase text-[#B65980] font-semibold">Read <ArrowUpRight size={13}/></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}
